import {
    DataTable,
    EditButton,
    ReferenceManyField
} from '@/components/admin';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Check, ClipboardList, Clock, X } from 'lucide-react';
import { useNotify, useRecordContext, useRefresh, useUpdate } from 'ra-core';

const QA_STATUS_STYLES: Record<string, string> = {
    passed: 'bg-emerald-100 text-emerald-700 border border-emerald-200',
    failed: 'bg-red-100 text-red-700 border border-red-200',
    pending: 'bg-amber-100 text-amber-700 border border-amber-200',
};

const QAStatusBadge = () => {
    const record = useRecordContext();
    const status = (record?.qa_status as string) || 'pending';
    return (
        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${QA_STATUS_STYLES[status] || QA_STATUS_STYLES.pending}`}>
            {status}
        </span>
    );
};

/**
 * Per-item accept / reject / pending controls (GRN #5). Accept marks the whole
 * received qty as accepted (passed); Reject marks it all rejected (failed);
 * Pending resets. Quantities can still be fine-tuned via the row's full QA
 * inspection form (Edit).
 */
const QAItemActions = () => {
    const record = useRecordContext<any>();
    const [update, { isPending }] = useUpdate();
    const notify = useNotify();
    const refresh = useRefresh();

    if (!record) return null;

    const setQa = (qa_status: 'passed' | 'failed' | 'pending') => {
        const received = Number(record.received_qty) || 0;
        const data =
            qa_status === 'passed'
                ? { qa_status, accepted_qty: received, rejected_qty: 0 }
                : qa_status === 'failed'
                  ? { qa_status, accepted_qty: 0, rejected_qty: received }
                  : { qa_status, accepted_qty: 0, rejected_qty: 0 };

        update(
            'grn_items',
            { id: record.id, data, previousData: record },
            {
                onSuccess: () => {
                    notify(`Item marked ${qa_status}`, { type: 'success' });
                    refresh();
                },
                onError: () => notify('Failed to update item QA', { type: 'error' }),
            },
        );
    };

    const status = record.qa_status || 'pending';

    return (
        <div className="flex items-center gap-1.5">
            <Button
                size="sm"
                variant="outline"
                className={`h-7 px-2 ${status === 'passed' ? 'bg-emerald-600 text-white hover:bg-emerald-700 border-emerald-600' : 'border-emerald-200 text-emerald-700 hover:bg-emerald-50'}`}
                disabled={isPending}
                onClick={() => setQa('passed')}
            >
                <Check className="w-3.5 h-3.5 mr-1" /> Accept
            </Button>
            <Button
                size="sm"
                variant="outline"
                className={`h-7 px-2 ${status === 'failed' ? 'bg-red-600 text-white hover:bg-red-700 border-red-600' : 'border-red-200 text-red-700 hover:bg-red-50'}`}
                disabled={isPending}
                onClick={() => setQa('failed')}
            >
                <X className="w-3.5 h-3.5 mr-1" /> Reject
            </Button>
            <Button
                size="sm"
                variant="outline"
                className={`h-7 px-2 ${status === 'pending' ? 'bg-amber-500 text-white hover:bg-amber-600 border-amber-500' : 'border-amber-200 text-amber-700 hover:bg-amber-50'}`}
                disabled={isPending}
                onClick={() => setQa('pending')}
            >
                <Clock className="w-3.5 h-3.5 mr-1" /> Pending
            </Button>
            <EditButton label="" />
        </div>
    );
};

export const GRNItemsList = () => (
    <Card className="mt-8">
        <CardHeader>
            <CardTitle className="text-sm font-bold uppercase tracking-wider text-slate-500 flex items-center gap-2">
                <ClipboardList className="w-4 h-4" /> Received Items & QA
            </CardTitle>
        </CardHeader>
        <CardContent>
            <ReferenceManyField reference="grn_items" target="grn_id">
                <DataTable className="bg-white">
                    <DataTable.Col source="po_item_description" label="Item Description" />
                    <DataTable.Col source="ordered_qty" label="Ordered" />
                    <DataTable.Col source="received_qty" label="Received" />
                    <DataTable.Col source="accepted_qty" label="Accepted" />
                    <DataTable.Col source="rejected_qty" label="Rejected" />
                    <DataTable.Col label="QA Status">
                        <QAStatusBadge />
                    </DataTable.Col>
                    <DataTable.Col label="Actions">
                        <QAItemActions />
                    </DataTable.Col>
                </DataTable>
            </ReferenceManyField>
        </CardContent>
    </Card>
);
